import type { Category, Article } from '@/types';

export const categories: Category[] = [
  {
    slug: 'contrast-visual-design',
    name: '视觉反差设计',
    description: '探索色彩、构图、排版中的反差美学，用视觉冲击力打造令人过目不忘的设计作品。',
    icon: 'ri-contrast-2-line',
    image: 'https://readdy.ai/api/search-image?query=Modern%20graphic%20design%20studio%20with%20bold%20contrasting%20colors%20black%20and%20white%20composition%20geometric%20shapes%20minimalist%20posters%20on%20wall%20warm%20accent%20lighting%20creative%20workspace%20artistic%20atmosphere%20professional%20design%20environment%20clean%20lines%20dramatic%20shadows&width=800&height=500&seq=cat-visual-design&orientation=landscape',
  },
  {
    slug: 'contrast-photography',
    name: '强反差摄影构图',
    description: '光影、虚实、远近、动静——用反差思维重构镜头语言，拍出直击人心的摄影作品。',
    icon: 'ri-camera-lens-line',
    image: 'https://readdy.ai/api/search-image?query=Dramatic%20black%20and%20white%20photography%20composition%20with%20strong%20light%20and%20shadow%20contrast%20professional%20camera%20on%20tripod%20in%20studio%20minimalist%20setup%20artistic%20photography%20gallery%20wall%20with%20contrasting%20images%20warm%20tone%20accents%20creative%20atmosphere&width=800&height=500&seq=cat-photography&orientation=landscape',
  },
  {
    slug: 'contrast-color',
    name: '反差色彩搭配',
    description: '冷暖、明暗、饱和度——掌握色彩反差的底层逻辑，让配色方案自带张力和节奏感。',
    icon: 'ri-palette-line',
    image: 'https://readdy.ai/api/search-image?query=Color%20theory%20palette%20wheel%20with%20vibrant%20contrasting%20color%20swatches%20warm%20and%20cool%20tones%20artistic%20arrangement%20on%20white%20surface%20design%20studio%20aesthetic%20minimalist%20composition%20professional%20color%20grading%20tools%20natural%20light&width=800&height=500&seq=cat-color&orientation=landscape',
  },
  {
    slug: 'contrast-copywriting',
    name: '反差文案技巧',
    description: '用文字制造意外与张力，从标题到正文，让反差成为你的文案放大器。',
    icon: 'ri-quill-pen-line',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20writing%20desk%20with%20fountain%20pen%20and%20notebook%20creative%20copywriting%20workspace%20warm%20lighting%20minimalist%20aesthetic%20vintage%20typewriter%20alongside%20modern%20laptop%20contrasting%20old%20and%20new%20artistic%20composition%20soft%20shadows&width=800&height=500&seq=cat-copywriting&orientation=landscape',
  },
  {
    slug: 'contrast-marketing',
    name: '反差营销策略',
    description: '打破常规的品牌定位与传播策略，用反差思维制造话题，实现低成本高传播的营销效果。',
    icon: 'ri-megaphone-line',
    image: 'https://readdy.ai/api/search-image?query=Creative%20marketing%20strategy%20brainstorming%20session%20whiteboard%20with%20bold%20contrasting%20ideas%20modern%20office%20setting%20warm%20lighting%20team%20collaboration%20visuals%20digital%20marketing%20concepts%20innovative%20branding%20posters%20artistic%20workspace&width=800&height=500&seq=cat-marketing&orientation=landscape',
  },
  {
    slug: 'contrast-persona',
    name: '反差感人设',
    description: '人物形象的多面性塑造——温柔与锋利、脆弱与坚韧，如何用反差打造令人难忘的角色。',
    icon: 'ri-user-star-line',
    image: 'https://readdy.ai/api/search-image?query=Artistic%20portrait%20photography%20showing%20dual%20personality%20contrast%20soft%20and%20sharp%20lighting%20split%20composition%20warm%20and%20cool%20tones%20creative%20character%20design%20concept%20minimalist%20studio%20backdrop%20dramatic%20emotional%20expression&width=800&height=500&seq=cat-persona&orientation=landscape',
  },
];

export const articles: Article[] = [
  {
    slug: 'color-contrast-basics',
    title: '配色中的反差哲学：为什么你的设计总是"太平了"？',
    excerpt: '从色彩理论的底层逻辑出发，理解冷暖、明暗、饱和度的三元反差体系，让你的配色方案立刻拥有节奏感和视觉重心。',
    category: 'contrast-color',
    author: '林觉非',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20male%20designer%20in%20his%2030s%20warm%20natural%20lighting%20simple%20cream%20background%20confident%20expression%20minimalist%20style&width=120&height=120&seq=author-lin&orientation=squarish',
    date: '2026-08-10',
    readTime: '8分钟',
    image: 'https://readdy.ai/api/search-image?query=Bold%20color%20contrast%20design%20composition%20vibrant%20warm%20orange%20against%20deep%20cool%20teal%20abstract%20geometric%20shapes%20modern%20graphic%20design%20aesthetic%20minimalist%20layout%20with%20white%20space%20professional%20color%20theory%20demonstration%20artistic%20editorial%20style&width=1200&height=630&seq=article-color-basics&orientation=landscape',
    featured: true,
    trending: true,
    content: {
      intro: '在设计的世界里，"太平了"大概是设计师最怕听到的三个字。作品缺乏层次、没有重心、看完即忘——这些问题的根源，往往出在配色上。而解决配色平庸的最强武器，就是"反差"。',
      sections: [
        {
          heading: '色彩反差的三重维度',
          body: '色彩反差并非简单地"把对比度拉满"。真正的色彩反差设计，需要在三个维度上精准调控：色相维度、明度维度和饱和度维度。这三个维度彼此独立又相互影响，构成了色彩反差的完整体系。',
        },
        {
          heading: '色相反差：冷暖的对话',
          body: '色相环上的对立色天生具有最强的反差感。当暖橙与冷蓝并置，画面立刻产生了视觉张力。但高手的做法不是简单地使用互补色，而是通过主色调与点缀色的精妙配比，让反差服务于画面叙事。一张以暖色为主的画面中，一处冷色点缀就是视觉的锚点。',
        },
        {
          heading: '明度反差：光与影的节奏',
          body: '明度反差是画面层次感的根基。研究表明，人眼对明度变化的敏感度远高于对色相变化的敏感度。优秀的设计作品中，即使去掉所有色彩，仅保留明度关系，画面依然精彩。这就是为什么设计大师们总说："先做好黑白灰，再加颜色。"',
        },
        {
          heading: '饱和度反差：克制中的爆发',
          body: '大面积的低饱和色调中，一块高饱和色彩会产生惊人的视觉能量。这种"以克制换爆发"的策略，是高级感配色方案的核心密码。日本设计中的"留白美学"、北欧设计中的"莫兰迪色系"，本质上都是在用低饱和铺垫，为高饱和的焦点蓄力。',
        },
        {
          heading: '实战：三个立即可用的反差配色公式',
          body: '公式一：70%主色 + 20%辅助色 + 10%反差色。这是经典的6:3:1配色法则，那10%的反差色就是画面的灵魂所在。公式二：暗底亮字 + 亮底暗字交替。通过明度关系的段落化切换，让长内容页面始终保持阅读节奏。公式三：暖调画面 + 冷调点缀。这是最符合人眼视觉习惯的反差组合，天然具有舒适而醒目的效果。',
        },
      ],
      conclusion: '反差不是目的，而是手段。真正好的反差设计，是让观众在"被吸引"之后，还能感受到画面内在的和谐与秩序。从今天开始，试着用这三重维度的反差视角重新审视你的作品，你会发现一个全新的设计世界。',
    },
  },
  {
    slug: 'photography-light-shadow',
    title: '光影游戏：如何用明暗反差拍出电影感大片',
    excerpt: '电影感的秘密藏在光影的反差里。从伦勃朗光到剪影美学，掌握光比控制的黄金法则，让你的摄影作品自带叙事感。',
    category: 'contrast-photography',
    author: '陈见山',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20male%20photographer%20in%20his%2040s%20warm%20studio%20lighting%20simple%20background%20artistic%20look%20glasses%20minimalist%20style&width=120&height=120&seq=author-chen&orientation=squarish',
    date: '2026-08-08',
    readTime: '12分钟',
    image: 'https://readdy.ai/api/search-image?query=Dramatic%20chiaroscuro%20lighting%20photography%20strong%20light%20beam%20cutting%20through%20darkness%20cinematic%20atmosphere%20dust%20particles%20in%20light%20warm%20golden%20tone%20against%20deep%20shadows%20artistic%20black%20and%20white%20style%20film%20noir%20aesthetic&width=1200&height=630&seq=article-photo-light&orientation=landscape',
    featured: true,
    trending: true,
    content: {
      intro: '你有没有想过，为什么有些照片看一眼就觉得"有电影感"？答案往往不在器材、不在场景，而在于光。准确地说，在于光与影的反差关系。',
      sections: [
        {
          heading: '光比：电影感的数学基础',
          body: '光比是主光与辅光之间的亮度比值。电影摄影师通常使用4:1到8:1的光比来营造戏剧性。当光比超过8:1，画面开始出现强烈的明暗反差，这正是"电影感"的核心来源。理解光比，就掌握了光影反差的定量表达。',
        },
        {
          heading: '伦勃朗光：三角区的魅力',
          body: '伦勃朗光以其标志性的"三角光斑"闻名——在暗侧脸颊上，一道小小的三角形高光区域。这种布光方式的精髓在于：通过精准的光影反差，让面部结构呈现出雕塑般的立体感。照片中的人物仿佛从黑暗中浮现，自带故事感。',
        },
        {
          heading: '剪影：极致的减法艺术',
          body: '剪影是反差摄影的终极形态——完全放弃暗部细节，只保留轮廓。当主体化为一道黑色剪影，观众的目光被引导至形状、姿态和背景的叙事中。好的剪影作品，是用最少的视觉信息，传递最丰富的情感。',
        },
        {
          heading: '逆光与边缘光：反差中的温柔',
          body: '并非所有反差都要硬朗。逆光拍摄时，主体边缘被光线勾勒出一条金边——这是反差摄影中最温柔的表达。它与背景的亮部形成对比，却不至于淹没主体的存在。掌握逆光拍摄，你的照片会立刻拥有一种"被光亲吻"的质感。',
        },
      ],
      conclusion: '光影反差的掌控，本质上是对"可见与不可见"之间边界的拿捏。多一分则过曝，少一分则欠曝——而恰到好处的那一档曝光，就是你的个人风格所在。',
    },
  },
  {
    slug: 'copywriting-surprise',
    title: '文案的"意料之外"：七种反差句式让你的文字瞬间抓人',
    excerpt: '消费者每天接触6000条广告信息，如何让你的文案脱颖而出？答案藏在"预期违背"里——用反差句式制造阅读惊喜。',
    category: 'contrast-copywriting',
    author: '苏语冰',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20female%20copywriter%20in%20her%2030s%20warm%20natural%20lighting%20simple%20cream%20background%20confident%20smile%20creative%20professional%20minimalist%20style&width=120&height=120&seq=author-su&orientation=squarish',
    date: '2026-08-05',
    readTime: '10分钟',
    image: 'https://readdy.ai/api/search-image?query=Creative%20copywriting%20concept%20bold%20typography%20on%20paper%20contrasting%20words%20black%20ink%20on%20white%20surface%20elegant%20pen%20alongside%20digital%20screen%20modern%20editorial%20design%20warm%20lighting%20minimalist%20workspace%20artistic%20composition&width=1200&height=630&seq=article-copywriting&orientation=landscape',
    featured: false,
    trending: true,
    content: {
      intro: '在信息过载的时代，平庸的文案等于零。用户的大脑会自动过滤掉那些"可预测"的信息——而反差的本质，就是对预期的违背。当你的文案让读者"没想到"，你就赢了第一步。',
      sections: [
        {
          heading: '反差句式一：大小对比法',
          body: '"花了三年时间，只为做好一碗面。"时间的大与产品的小形成强烈反差，瞬间让读者对这碗面产生了敬意和好奇。这种句式的核心是用"大投入"锚定"小产出"，制造价值感。',
        },
        {
          heading: '反差句式二：身份翻转法',
          body: '"一个不会写代码的设计师，不是一个好产品经理。"通过看似矛盾的职业身份排列，制造认知冲突。这种句式适用于品牌跨界、个人IP打造等场景，用身份的"不合理"来传递"独特价值主张"。',
        },
        {
          heading: '反差句式三：价格反差法',
          body: '"比一杯咖啡还便宜的健身教练。"用熟悉的价格参照物（咖啡）与高价服务（私教）形成反差，降低用户的价格感知门槛。关键是选择大众熟知的低价参照物。',
        },
        {
          heading: '反差句式四：数量反差法',
          body: '"5000次测试，只为1毫米的改进。"大数字与小单位的反差组合，让"精益求精"这个抽象概念变得可感知。在品牌故事和产品文案中，这种句式屡试不爽。',
        },
        {
          heading: '反差句式五：否定前置法',
          body: '"这不是一篇教你赚钱的文章。"先用否定制造悬念，再引出真正要说的内容。这种"以退为进"的句式天然具有高打开率，因为它激起了读者"那是什么？"的好奇心。',
        },
      ],
      conclusion: '反差文案的终极心法：先让读者停下，再让读者记住。停止是第一目标，记住是第二目标。当你学会了在标题和前30个字中制造反差，你的文案就已经成功了一半。',
    },
  },
  {
    slug: 'brand-contrast-strategy',
    title: '品牌定位的逆向思维：为什么"反差萌"能一年卖出10个亿？',
    excerpt: '当所有品牌都在强调"专业"时，你选择"不正经"反而赢了。深度拆解泡泡玛特、蜜雪冰城、五菱宏光的反差营销密码。',
    category: 'contrast-marketing',
    author: '林觉非',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20male%20designer%20in%20his%2030s%20warm%20natural%20lighting%20simple%20cream%20background%20confident%20expression%20minimalist%20style&width=120&height=120&seq=author-lin&orientation=squarish',
    date: '2026-08-02',
    readTime: '15分钟',
    image: 'https://readdy.ai/api/search-image?query=Creative%20brand%20marketing%20visualization%20with%20contrasting%20elements%20luxury%20item%20next%20to%20playful%20toy%20bold%20graphic%20design%20modern%20retail%20display%20warm%20lighting%20innovative%20branding%20concept%20minimalist%20composition%20editorial%20style&width=1200&height=630&seq=article-brand&orientation=landscape',
    featured: true,
    trending: false,
    content: {
      intro: '2023年，泡泡玛特营收突破60亿元。一个卖盲盒的公司，凭什么？答案是"反差"。当一个品牌提供的体验与消费者的常规预期形成巨大落差时，传播就自然而然地发生了。',
      sections: [
        {
          heading: '反差营销的底层逻辑',
          body: '人类大脑对"预期违背"有着天然的注意力倾斜。神经科学的研究表明，当人们遇到与预期不符的信息时，大脑的前扣带皮层会被激活——这正是"惊讶"和"好奇"的神经基础。反差营销，本质上是利用这种认知机制来获取注意力资源。',
        },
        {
          heading: '案例一：蜜雪冰城——"土到极致就是潮"',
          body: '当喜茶、奈雪们在追求"高级感"时，蜜雪冰城选择了一条截然相反的路——极致下沉。3元冰淇淋、魔性主题曲、"你爱我我爱你蜜雪冰城甜蜜蜜"……这些看起来"土"到不行的操作，恰恰构成了与"精品茶饮"主流的巨大反差。结果呢？蜜雪冰城在全球拥有超过3万家门店，成为中国最大的茶饮品牌。',
        },
        {
          heading: '案例二：五菱宏光MINIEV——"老头乐"变身潮流单品',
          body: '五菱宏光，一个以面包车闻名的大众品牌，推出了售价不到3万的微型电动车。但它的营销打法完全不同——小红书种草、马卡龙配色、联名时尚品牌。这种"低价车"与"时尚单品"之间的反差，让五菱MINIEV在年轻人中彻底出圈，销量一度超越特斯拉Model 3。',
        },
        {
          heading: '案例三：泡泡玛特——"玩具"还是"投资品"？',
          body: '59元一个的塑料小人，凭什么让人排队抢购？泡泡玛特巧妙地将"儿童玩具"的品类印象，与"收藏投资"的消费动机结合。隐藏款的稀缺性制造了"博彩式"的购买快感，而二级市场的惊人溢价又不断强化这种反差叙事的可信度。当消费者不再用"玩具"而是用"潮玩"来称呼它时，品类反差已经完成。',
        },
      ],
      conclusion: '反差营销的本质不是"瞎搞"，而是在深刻理解行业共识之后，找到那个"大家都不敢做"的方向，然后把它做到极致。正如营销大师Al Ries所说："要么差异化，要么死亡。"而反差，是最锋利的差异化武器。',
    },
  },
  {
    slug: 'character-design-duality',
    title: '人物的两面性：如何用"反差感人设"让角色活起来',
    excerpt: '从《甄嬛传》的华妃到《狂飙》的高启强——深入拆解成功角色背后的人设反差密码，让你的角色塑造告别扁平化。',
    category: 'contrast-persona',
    author: '苏语冰',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20female%20copywriter%20in%20her%2030s%20warm%20natural%20lighting%20simple%20cream%20background%20confident%20smile%20creative%20professional%20minimalist%20style&width=120&height=120&seq=author-su&orientation=squarish',
    date: '2026-07-28',
    readTime: '13分钟',
    image: 'https://readdy.ai/api/search-image?query=Artistic%20dual%20portrait%20concept%20showing%20contrasting%20personalities%20split%20face%20composition%20warm%20and%20cool%20toned%20halves%20dramatic%20lighting%20character%20design%20illustration%20minimalist%20dark%20background%20emotional%20depth&width=1200&height=630&seq=article-character&orientation=landscape',
    featured: false,
    trending: false,
    content: {
      intro: '为什么有些角色你看了就忘，而有些角色却让你久久难忘？答案往往藏在"反差"两个字里。扁平的角色只有一种颜色，而立体的角色则有光有影、有明有暗——这正是"反差感人设"的魅力所在。',
      sections: [
        {
          heading: '什么是"反差感人设"？',
          body: '反差感人设，指的是角色身上存在明显的、令人意外的性格或行为对立面。比如一个外表冷酷的杀手却热爱小动物，一个温柔的母亲在关键时刻展现出惊人的坚韧。这种反差不是矛盾，而是人性的真实剖面——每个人都是由多个"自我"构成的复杂存在。',
        },
        {
          heading: '反差设计的三层结构',
          body: '第一层：表象层——角色呈现给外界的"人设"。第二层：内核层——角色真实的内心世界。第三层：触发层——什么情况下内核会突破表象。三层结构的精妙之处在于，它让反差不是随机的矛盾，而是有逻辑的性格纵深。',
        },
        {
          heading: '华妃的悲剧：强势外表下的脆弱核心',
          body: '《甄嬛传》中华妃的塑造堪称反差人设的教科书。表面上的嚣张跋扈、不可一世，但观众之所以对这个反派又恨又怜，正是因为编剧不断展示她的脆弱内核——对皇上的痴情、对不孕的自卑。当观众看到了强势之下的脆弱，角色就获得了深度。',
        },
        {
          heading: '高启强的复杂：善恶的灰色地带',
          body: '《狂飙》中高启强的魅力在于，他不是一个纯粹的坏人。从被欺负的鱼贩到黑帮大佬，他的每一次"变坏"都有其不得已的理由。这种"好人变坏"的过程本身就是一种叙事反差，它让观众在道德判断中产生了撕裂感——而这正是角色魅力的来源。',
        },
      ],
      conclusion: '创作反差人设的黄金法则：给每个角色至少一个"不像TA"的细节。因为这个细节，就是观众理解角色复杂性的入口。记住，完美的角色不是没有缺点，而是有恰到好处的"不合理"。',
    },
  },
  {
    slug: 'minimalist-maximalist',
    title: '极简与极繁的碰撞：2026年最值得关注的视觉反差趋势',
    excerpt: '当极简主义撞上极繁主义，设计界正在发生一场静悄悄的革命。掌握这股"两极融合"的新趋势，让你的设计站在时代前沿。',
    category: 'contrast-visual-design',
    author: '陈见山',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20male%20photographer%20in%20his%2040s%20warm%20studio%20lighting%20simple%20background%20artistic%20look%20glasses%20minimalist%20style&width=120&height=120&seq=author-chen&orientation=squarish',
    date: '2026-07-25',
    readTime: '9分钟',
    image: 'https://readdy.ai/api/search-image?query=Bold%20visual%20contrast%20between%20minimalist%20clean%20white%20space%20and%20maximalist%20vibrant%20ornate%20patterns%20modern%20interior%20design%20concept%20split%20composition%20warm%20gold%20accents%20on%20neutral%20background%20artistic%20editorial%20photography&width=1200&height=630&seq=article-minimalist&orientation=landscape',
    featured: false,
    trending: true,
    content: {
      intro: '过去十年，极简主义几乎统治了设计界。但2026年，一股新的风潮正在兴起——不是极简或极繁的二选一，而是两者的碰撞与融合。这种"两极反差"的设计语言，正在成为新一代设计师的标志性风格。',
      sections: [
        {
          heading: '趋势背景：极简主义的疲劳',
          body: '当所有品牌都在做"去装饰化"的时候，"干净"反而变成了"无聊"。消费者开始渴望视觉上的丰富度和温度感。但极繁主义的回归不是简单地回到过去——它需要与极简主义进行对话和融合，形成一种新的设计语法。',
        },
        {
          heading: '两极融合的核心手法',
          body: '手法一：大面积留白 + 局部密集纹理。在极简的框架中置入一处极繁的焦点，让视觉张力达到顶峰。手法二：中性色基调 + 高饱和点缀。克制与放纵在同一个画面中共存。手法三：简洁布局 + 繁复字体。用排版的反差创造独特的视觉节奏。',
        },
        {
          heading: '品牌应用案例',
          body: '奢侈品牌Gucci在2025年的视觉升级中，将经典的极繁花纹与极简的产品陈列结合，创造出"热闹中的安静"这一反差美学。新兴护肤品牌Aesop则反其道而行——极简的瓶身设计搭配复杂的文字说明标签。"简"与"繁"在不同维度上交织。',
        },
      ],
      conclusion: '设计的未来不属于任何单一风格，而属于那些能在对立中找到融合点的创作者。极简与极繁的碰撞，本质上是秩序与自由的对话——而最美的设计，就诞生在两者之间的那条线上。',
    },
  },
  {
    slug: 'typography-contrast',
    title: '字体排版的反差艺术：大小、粗细、字间距的情绪表达',
    excerpt: '排版不只是"把字放上去"。通过字体大小、粗细、间距的精确反差控制，你可以让文字自带情绪和节奏感。',
    category: 'contrast-visual-design',
    author: '林觉非',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20male%20designer%20in%20his%2030s%20warm%20natural%20lighting%20simple%20cream%20background%20confident%20expression%20minimalist%20style&width=120&height=120&seq=author-lin&orientation=squarish',
    date: '2026-07-20',
    readTime: '7分钟',
    image: 'https://readdy.ai/api/search-image?query=Bold%20typography%20design%20with%20dramatic%20size%20contrast%20large%20display%20text%20against%20tiny%20body%20copy%20elegant%20serif%20and%20clean%20sans-serif%20pairing%20minimalist%20layout%20warm%20paper%20texture%20background%20editorial%20design%20aesthetic&width=1200&height=630&seq=article-typo&orientation=landscape',
    featured: false,
    trending: false,
    content: {
      intro: '排版设计中最被低估的力量，是"对比"。当你把标题放大到72pt，正文缩小到14pt——这种极端的尺寸反差本身就是一种强烈的视觉表达。今天我们来聊聊字体排版中的反差艺术。',
      sections: [
        {
          heading: '尺寸反差：大小之间的张力',
          body: '大标题与小正文之间的尺寸反差，是最基本的排版对比手法。但真正的高手会在"大"中再分大小——标题内部的主副关系，正文内部的层级递进。尺寸反差的本质是信息层级的视觉化表达。',
        },
        {
          heading: '粗细反差：黑体与宋体的情绪对白',
          body: '粗体传递力量与自信，细体传递优雅与克制。当一段文字中粗体与细体交替出现，读者的视线会自动跟随粗细变化的节奏流动。这种"黑白灰"的灰度反差，是中文字体排版中最迷人的部分。',
        },
        {
          heading: '间距反差：密与疏的呼吸节奏',
          body: '紧密排列的文字营造紧张感和信息密度，宽松的间距则带来呼吸感和高级感。在同一个版面中，标题紧凑、正文舒朗、注释稀疏——这三种间距形成"紧-松-更松"的反差节奏，让版面有了"呼吸"。',
        },
        {
          heading: '中西文混排：文化符号的反差',
          body: '中文与英文在同一版面中的并置，本身就是一种文化层面的反差。宋体配Sans-serif、黑体配Serif——跨字体的搭配需要兼顾可读性与美感。好的中西文混排，是两种书写文明的对话。',
        },
      ],
      conclusion: '排版不是"排整齐就行了"，而是一场精心编排的视觉交响乐。每一个字号的选择、每一个粗细的调整、每一个间距的控制，都是在谱写属于你的版面节奏。',
    },
  },
  {
    slug: 'emotional-contrast-storytelling',
    title: '从大喜到大悲：情绪反差如何成为最强大的叙事武器',
    excerpt: '最好的故事不是一直让你笑或一直让你哭，而是让你"笑着笑着就哭了"。拆解情绪反差叙事的黄金结构。',
    category: 'contrast-copywriting',
    author: '苏语冰',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Chinese%20female%20copywriter%20in%20her%2030s%20warm%20natural%20lighting%20simple%20cream%20background%20confident%20smile%20creative%20professional%20minimalist%20style&width=120&height=120&seq=author-su&orientation=squarish',
    date: '2026-07-15',
    readTime: '11分钟',
    image: 'https://readdy.ai/api/search-image?query=Emotional%20contrast%20concept%20art%20showing%20joy%20and%20sorrow%20merged%20in%20one%20frame%20warm%20golden%20light%20against%20dark%20shadows%20minimalist%20composition%20theatrical%20lighting%20artistic%20abstract%20representation%20of%20duality%20of%20human%20emotions&width=1200&height=630&seq=article-emotion&orientation=landscape',
    featured: false,
    trending: false,
    content: {
      intro: '你有没有过这样的体验：看一部喜剧，却在某个瞬间突然泪流满面。这不是你的问题——这是创作者精心设计的"情绪反差"。情绪反差是所有叙事形式中最强大的武器，因为它直接作用于人的共情系统。',
      sections: [
        {
          heading: '情绪反差的神经科学',
          body: '研究表明，情绪体验的强度不取决于情绪的"绝对值"，而取决于情绪的"变化幅度"。从平静到狂喜的跨度越大，体验越强烈。这解释了为什么"苦尽甘来"的故事永远比一帆风顺的故事更打动人心——反差制造了体验的纵深。',
        },
        {
          heading: '情绪反差的三幕结构',
          body: '第一幕：建立情绪基线——让观众进入一种稳定的情绪状态。第二幕：情绪翻转——通过情节转折制造与基线相反的情绪体验。第三幕：情绪融合——两种对立的情绪在观众心中交织，产生"悲喜交加"的复杂感受。这就是"笑着笑着就哭了"的底层结构。',
        },
        {
          heading: '品牌故事中的情绪反差',
          body: '耐克最经典的广告不是展示运动员夺冠的荣耀时刻，而是展示他们失败、受伤、被质疑的至暗时刻。当这些"低谷"与最终的"高峰"形成情绪反差时，品牌精神获得了前所未有的感染力。这就是为什么"从地下室到聚光灯"的创业故事永远有效——情绪反差让观众完成了从"旁观者"到"参与者"的身份转换。',
        },
      ],
      conclusion: '情绪反差的美妙之处在于，它不需要惊天动地的大事件。一个微笑之后的沉默，一句玩笑之后的叹息——生活中最细微的情绪反差，往往蕴含着最深刻的故事力量。学会捕捉和放大这些时刻，你就掌握了叙事的顶级技巧。',
    },
  },
];

export const getArticlesByCategory = (categorySlug: string): Article[] => {
  return articles.filter((a) => a.category === categorySlug);
};

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((a) => a.slug === slug);
};

export const getCategoryBySlug = (slug: string): Category | undefined => {
  return categories.find((c) => c.slug === slug);
};

export const getFeaturedArticles = (): Article[] => {
  return articles.filter((a) => a.featured);
};

export const getTrendingArticles = (): Article[] => {
  return articles.filter((a) => a.trending);
};